import type { PieceButton, PieceButtonHole, SceneEntity } from '@seddi/scene';

import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  type PieceButtonRenderable,
  attachButton,
  attachGarmentGeometry,
  detachGarmentGeometry,
  isCloseToHole,
  updateTransformFromPiece,
} from '../renderables/pieceButtonRenderable';
import type { PieceRenderable } from '../renderables/pieceRenderable';
import { findButton } from '../scene/scene';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';

/**
 * Update renderable.
 * @param renderable - Renderable.
 * @param nextProps - Button next.
 * @param prevProps - Button prev.
 * @param pieceRenderable - Piece renderable.
 * @param frame - Frame.
 */
export const update1st = (
  renderable: PieceButtonRenderable,
  nextProps: PieceButton,
  prevProps: PieceButton | undefined,
  pieceRenderable: PieceRenderable,
  frame: SceneReconcilerFrame,
): boolean => {
  const {
    nextGarmentGeometry,
    nextScene,
    nextUseGarmentGeometry,
    prevGarmentGeometry,
    prevUseGarmentGeometry,
    resources,
  } = frame;

  let dirty = false;

  // Update from garment geometry.
  if (nextGarmentGeometry !== prevGarmentGeometry) {
    if (prevGarmentGeometry) {
      detachGarmentGeometry(renderable);
    }
    if (nextGarmentGeometry) {
      attachGarmentGeometry(renderable, nextProps, nextGarmentGeometry, resources);
    }
    dirty = true;
  }

  const diffProps = PropsDiff.diff(nextProps, prevProps);

  // Update resource.
  if (diffProps.includes('resourceID')) {
    const id = nextProps.resourceID;

    if (resources.findButton(id)) {
      attachButton(renderable, nextProps, resources);
      dirty = true;
    } else {
      if (!resources.hasButtonRequest(id)) {
        const b = findButton(nextScene, id);
        resources.loadButtonDeferred(b);
      }
    }
  }

  // Update transform.
  const isTransformDirty =
    !nextUseGarmentGeometry &&
    ((!nextUseGarmentGeometry && prevUseGarmentGeometry) ||
      frame.entities.get(renderable.id)!.isGeometryDirty ||
      diffProps.includes('resourceID') ||
      diffProps.includes('transform'));

  if (isTransformDirty) {
    // Update transform depends on piece geometry and button height.
    const nextButton = findButton(nextScene, nextProps.resourceID)!;
    updateTransformFromPiece(renderable, nextProps, nextButton, pieceRenderable);
    dirty = true;
  }

  return dirty;
};

/**
 * Update renderable.
 * @param renderable - Renderable.
 * @param nextProps - Button next.
 * @param prevProps - Button prev.
 * @param pieceRenderable - Piece renderable.
 * @param frame - Frame.
 */
export const update2nd = (
  renderable: PieceButtonRenderable,
  nextProps: PieceButton,
  pieceRenderable: PieceRenderable,
  frame: SceneReconcilerFrame,
): boolean => {
  const { nextScene, nextUseGarmentGeometry } = frame;
  // Do nothing if garment geometry.
  if (nextUseGarmentGeometry) {
    return false;
  }

  // Do nothing if no dirty pair.
  const pair = nextProps.pair;
  const pairData = pair && findPair(pair, frame);
  if (!pairData) {
    return false;
  }

  const pairEntity = pairData.entity;
  const pairHole = pairData.hole;
  const pairRenderable: PieceRenderable = frame.scene.entities.get(pairEntity._id)!.piece;
  if (isCloseToHole(pieceRenderable, nextProps.transform, pairRenderable, pairHole.transform)) {
    const resource = findButton(nextScene, nextProps.resourceID)!;
    updateTransformFromPiece(renderable, pairHole, resource, pairRenderable);
  }
};

// Detail

type HoleData = { entity: SceneEntity; hole: PieceButtonHole };

/**
 * Find hole by ID.
 * @param id - Piece hole ID.
 * @param frame - Reconciler frame.
 */
const findPair = (id: string, frame: SceneReconcilerFrame): HoleData | undefined => {
  for (const e of frame.entities.values()) {
    if (!e.isGeometryDirty || !e.isPiece) {
      continue;
    }
    // Find corresponding hole.
    const entity = e.data;
    const hole = entity.components.garmentPiece.buttonHoles?.find((h) => h._id === id);
    if (hole) {
      return { entity, hole };
    }
  }
  return undefined;
};
